import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService, User } from '../../../core/services/auth.service';

@Component({
  selector: 'app-admin-security-dialog',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule,
    MatInputModule, MatSelectModule, MatFormFieldModule, MatIconModule, MatSnackBarModule
  ],
  template: `
    <h2 mat-dialog-title style="display:flex;align-items:center;gap:8px;color:#1a237e">
      <mat-icon>security</mat-icon> Sécurité : Mots de Passe
    </h2>
    <mat-dialog-content style="min-width:400px;padding-top:10px">
      <form [formGroup]="form" (ngSubmit)="submit()">
        
        <div *ngIf="loading" style="text-align:center;padding:20px;color:#888">
            Chargement des comptes...
        </div>

        <ng-container *ngIf="!loading">
            <mat-form-field appearance="outline" style="width:100%;margin-bottom:12px">
            <mat-label>Compte Utilisateur</mat-label>
            <mat-select formControlName="userId">
                <mat-option *ngFor="let u of users" [value]="u.id">
                <div style="display:flex;align-items:center;gap:8px">
                    <mat-icon [style.color]="u.role === 'admin' ? '#e74c3c' : '#00b0ff'">
                        {{ u.role === 'admin' ? 'admin_panel_settings' : 'person' }}
                    </mat-icon>
                    <strong>{{ u.username }}</strong> ({{ u.role }})
                </div>
                </mat-option>
            </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" style="width:100%;margin-bottom:12px">
            <mat-label>Nouveau mot de passe</mat-label>
            <input matInput [type]="hideNew ? 'password' : 'text'" formControlName="newPassword">
            <button type="button" mat-icon-button matSuffix (click)="hideNew = !hideNew">
                <mat-icon>{{hideNew ? 'visibility_off' : 'visibility'}}</mat-icon>
            </button>
            </mat-form-field>

            <mat-form-field appearance="outline" style="width:100%">
            <mat-label>Confirmer nouveau mot de passe</mat-label>
            <input matInput [type]="hideConfirm ? 'password' : 'text'" formControlName="confirmPassword">
            <button type="button" mat-icon-button matSuffix (click)="hideConfirm = !hideConfirm">
                <mat-icon>{{hideConfirm ? 'visibility_off' : 'visibility'}}</mat-icon>
            </button>
            </mat-form-field>
            
            <div *ngIf="form.hasError('passwordsMismatch') && form.get('confirmPassword')?.touched" style="color:#d32f2f;font-size:12px;margin-top:-10px;margin-bottom:10px">
            Les mots de passe ne correspondent pas.
            </div>
        </ng-container>

      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Annuler</button>
      <button mat-raised-button color="primary" [disabled]="form.invalid || submitting" (click)="submit()">
        <mat-icon *ngIf="!submitting">save</mat-icon>
        <span *ngIf="submitting" class="spinner"></span>
        Enregistrer
      </button>
    </mat-dialog-actions>
  `
})
export class AdminSecurityDialogComponent implements OnInit {
  form: FormGroup;
  users: User[] = [];
  loading = true;
  submitting = false;
  hideNew = true;
  hideConfirm = true;

  constructor(
    private fb: FormBuilder,
    private authSvc: AuthService,
    private snack: MatSnackBar,
    public dialogRef: MatDialogRef<AdminSecurityDialogComponent>
  ) {
    this.form = this.fb.group({
      userId: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(4)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit() {
    this.authSvc.getAllUsers().subscribe({
      next: (data) => {
        this.users = data;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('newPassword')?.value === g.get('confirmPassword')?.value
      ? null : { 'passwordsMismatch': true };
  }

  submit() {
    if (this.form.invalid) return;
    this.submitting = true;
    const { userId, newPassword } = this.form.value;

    this.authSvc.adminChangePassword(userId, newPassword).subscribe({
      next: () => {
        this.snack.open('Mot de passe mis à jour avec succès', 'OK', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: (e) => {
        this.snack.open(e.error?.error || 'Erreur lors de la mise à jour', 'OK', { duration: 4000 });
        this.submitting = false;
      }
    });
  }
}
